import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LuWallet, LuShoppingCart, LuBanknote, LuSmartphone, LuStore, LuPackageX,
  LuShieldAlert, LuCreditCard, LuClock, LuCircleAlert, LuMapPin, LuCircleCheck,
  LuChevronLeft, LuChevronRight, LuArrowRight,
} from 'react-icons/lu';
import { useAuth } from '../../context/AuthContext';
import API from '../../api/axios';
import AppLayout from '../../components/layout/AppLayout';
import { Page, PageHeader, Card, StatCard, Grid, Badge, EmptyState, Loader, Button } from '../../components/ui';
import { C, fmt, fmtF } from '../../theme';

const PAR_PAGE = 5;

export default function DashboardAdmin() {
  const { utilisateur } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [alertes, setAlertes] = useState([]);
  const [etablissements, setEtablissements] = useState([]);
  const [suspects, setSuspects] = useState(null);
  const [abonnements, setAbonnements] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => { chargerDonnees(); }, []);

  const chargerDonnees = async () => {
    const annee = new Date().getFullYear();
    try {
      const [rapportRes, alertesRes, etablissementsRes, suspectsRes, abosRes] = await Promise.all([
        API.get(`/rapports/chiffre-affaires?date_debut=${annee}-01-01&date_fin=${annee}-12-31`),
        API.get('/produits/alertes'),
        API.get('/etablissements'),
        API.get('/rapports/suspects'),
        API.get('/abonnements')
      ]);
      setStats(rapportRes.data.totaux);
      setAlertes(alertesRes.data.produits || []);
      setEtablissements(etablissementsRes.data.etablissements || []);
      setSuspects(suspectsRes.data);
      setAbonnements(abosRes.data);
    } catch (err) {
      console.error('Erreur chargement:', err);
    } finally {
      setChargement(false);
    }
  };

  const etabTries = [...etablissements].sort((a, b) =>
    new Date(b.created_at) - new Date(a.created_at)
  );
  const totalPages = Math.ceil(etabTries.length / PAR_PAGE);
  const etabPage = etabTries.slice((page - 1) * PAR_PAGE, page * PAR_PAGE);

  if (chargement) return <Loader label="Chargement du dashboard…" />;

  const resumeAbos = abonnements?.resume || {};
  const statsSuspects = suspects?.stats || {};

  return (
    <AppLayout role="admin">
      <Page>
        <PageHeader
          title="Vue globale"
          subtitle={`Bienvenue ${utilisateur?.prenom || ''} · ${new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`}
          actions={<Button variant="secondary" icon={LuArrowRight} onClick={() => navigate('/admin/rapports')}>Rapports détaillés</Button>}
        />

        {/* ── Activité commerciale ── */}
        <SectionLabel>Activité de l'année</SectionLabel>
        <Grid min={210} style={{ marginBottom: 24 }}>
          <StatCard icon={LuWallet}       label="Revenus" value={`${fmtF(stats?.total_general)}`} color={C.success} />
          <StatCard icon={LuShoppingCart} label="Ventes"             value={fmt(stats?.nombre_ventes)} color={C.info} />
          <StatCard icon={LuBanknote}     label="Espèces"            value={`${fmtF(stats?.total_especes)}`} color={C.warn} />
          <StatCard icon={LuSmartphone}   label="Mobile Money"       value={`${fmtF(stats?.total_mobile_money)}`} color={C.purple} />
        </Grid>

        {/* ── Santé de la plateforme ── */}
        <SectionLabel>Plateforme</SectionLabel>
        <Grid min={150} gap={10} style={{ marginBottom: 24 }}>
          <MiniKpi icon={LuStore}       label="Établissements"     value={etablissements.length}             color="#0E7490" onClick={() => navigate('/admin/etablissements')} />
          <MiniKpi icon={LuCreditCard}  label="Abonnements actifs" value={resumeAbos.actifs || 0}            color={C.success} onClick={() => navigate('/admin/abonnements')} />
          <MiniKpi icon={LuClock}       label="Expirent bientôt"   value={resumeAbos.expire_bientot || 0}    color={C.warn}    alert={resumeAbos.expire_bientot > 0} onClick={() => navigate('/admin/abonnements')} />
          <MiniKpi icon={LuCircleAlert} label="Abonnements expirés" value={resumeAbos.expires || 0}          color={C.danger}  alert={resumeAbos.expires > 0} onClick={() => navigate('/admin/abonnements')} />
          <MiniKpi icon={LuPackageX}    label="Alertes stock"      value={alertes.length}                    color={C.danger}  alert={alertes.length > 0} onClick={() => navigate('/admin/stocks')} />
          <MiniKpi icon={LuShieldAlert} label="Actes suspects"     value={suspects?.total || 0}              color="#B91C1C"   alert={suspects?.total > 0} />
        </Grid>

        <Grid min={360} style={{ marginBottom: 20 }}>
          {/* ── Établissements ── */}
          <Card
            title="Derniers établissements"
            action={<LinkButton onClick={() => navigate('/admin/etablissements')}>Tout voir</LinkButton>}
          >
            {etablissements.length === 0 ? (
              <EmptyState icon={LuStore} title="Aucun établissement" text="Créez le premier depuis la page Établissements." />
            ) : (
              <>
                {etabPage.map(etab => (
                  <div key={etab.id} style={rowStyle}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, display: 'grid', placeItems: 'center', backgroundColor: '#ECFEFF', color: '#0E7490', fontSize: 18 }}>
                      <LuStore aria-hidden />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: C.text, ...ellipsis }}>{etab.nom}</div>
                      <div style={{ fontSize: 12, color: C.textMuted, display: 'flex', alignItems: 'center', gap: 4, ...ellipsis }}>
                        <span style={{ textTransform: 'capitalize' }}>{etab.type}</span>
                        {etab.secteur_activite && <> · {etab.secteur_activite}</>}
                        {etab.adresse && <> · <LuMapPin aria-hidden style={{ flexShrink: 0 }} /> {etab.adresse}</>}
                      </div>
                    </div>
                    <Badge tone={etab.actif ? 'success' : 'danger'}>{etab.actif ? 'Actif' : 'Inactif'}</Badge>
                  </div>
                ))}

                {totalPages > 1 && (
                  <nav aria-label="Pagination" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 }}>
                    <span style={{ fontSize: 13, color: C.textMuted }}>Page {page} sur {totalPages}</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <IconBtn label="Page précédente" disabled={page === 1} onClick={() => setPage(p => p - 1)}><LuChevronLeft /></IconBtn>
                      <IconBtn label="Page suivante" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}><LuChevronRight /></IconBtn>
                    </div>
                  </nav>
                )}
              </>
            )}
          </Card>

          {/* ── Alertes stock ── */}
          <Card
            title="Alertes stock"
            action={alertes.length > 0 && <LinkButton onClick={() => navigate('/admin/stocks')}>Gérer les stocks</LinkButton>}
          >
            {alertes.length === 0 ? (
              <EmptyState icon={LuCircleCheck} title="Tous les stocks sont normaux" />
            ) : (
              alertes.slice(0, 6).map(produit => {
                const rupture = produit.quantite === 0;
                return (
                  <div key={produit.id} style={rowStyle}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: C.text, ...ellipsis }}>{produit.nom}</div>
                      <div style={{ fontSize: 12, color: C.textMuted, ...ellipsis }}>{produit.etablissement_nom} · seuil {produit.seuil_alerte}</div>
                    </div>
                    <Badge tone={rupture ? 'danger' : 'warn'}>
                      {rupture ? 'Rupture' : `${produit.quantite} restant${produit.quantite > 1 ? 's' : ''}`}
                    </Badge>
                  </div>
                );
              })
            )}
            {alertes.length > 6 && (
              <div style={{ fontSize: 13, color: C.textMuted, marginTop: 10 }}>+ {alertes.length - 6} autre{alertes.length - 6 > 1 ? 's' : ''} produit{alertes.length - 6 > 1 ? 's' : ''}</div>
            )}
          </Card>
        </Grid>

        {/* ── Surveillance ── */}
        <Card title="Comportements suspects">
          <Grid min={140} gap={10} style={{ marginBottom: 16 }}>
            {[
              { label: 'Annulations (24 h)',    valeur: statsSuspects.annulations_jour },
              { label: 'Pertes stock (24 h)',   valeur: statsSuspects.pertes_jour },
              { label: 'Désactivations (24 h)', valeur: statsSuspects.desactivations_jour },
              { label: 'Connexions (1 h)',      valeur: statsSuspects.connexions_recentes },
              { label: 'Modifs produits (1 h)', valeur: statsSuspects.modifs_recentes },
            ].map(item => (
              <div key={item.label} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 12px', backgroundColor: '#F8FAFC' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: item.valeur > 0 ? C.text : C.textMuted, fontVariantNumeric: 'tabular-nums' }}>{item.valeur || 0}</div>
                <div style={{ fontSize: 12, color: C.textMuted }}>{item.label}</div>
              </div>
            ))}
          </Grid>

          {!suspects?.derniers_actes?.length ? (
            <EmptyState icon={LuCircleCheck} title="Aucun comportement suspect détecté" />
          ) : (
            <div style={{ overflowX: 'auto', margin: '0 -20px -18px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 640 }}>
                <thead>
                  <tr style={{ backgroundColor: '#F8FAFC' }}>
                    {['Date', 'Utilisateur', 'Action', 'Détails'].map(h => <th key={h} style={thStyle}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {suspects.derniers_actes.map((acte, i) => (
                    <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td style={{ ...tdStyle, color: C.textSub, whiteSpace: 'nowrap' }}>
                        {new Date(acte.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600 }}>{acte.prenom} {acte.nom}</div>
                        <div style={{ color: C.textMuted, fontSize: 12 }}>{acte.telephone}</div>
                      </td>
                      <td style={tdStyle}>
                        <Badge tone="warn"><span style={{ textTransform: 'capitalize' }}>{acte.action.replace(/_/g, ' ')}</span></Badge>
                      </td>
                      <td style={{ ...tdStyle, color: C.textSub, fontSize: 13 }}>{acte.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </Page>
    </AppLayout>
  );
}

// ── Sous-composants ─────────────────────────────────────────────
function SectionLabel({ children }) {
  return <h2 style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.textMuted, margin: '0 0 10px' }}>{children}</h2>;
}

function MiniKpi({ icon: Icon, label, value, color, alert, onClick }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, width: '100%', height: '100%', textAlign: 'left',
        padding: '12px 14px', borderRadius: 12, backgroundColor: C.surface,
        border: `1px solid ${alert ? `color-mix(in srgb, ${color} 35%, white)` : C.border}`,
      }}
    >
      <Icon aria-hidden style={{ fontSize: 20, color, flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: C.text, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
        <div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.3 }}>{label}</div>
      </div>
    </Tag>
  );
}

function LinkButton({ children, onClick }) {
  return (
    <button onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'none', color: 'var(--accent)', fontWeight: 600, fontSize: 13, padding: '6px 4px' }}>
      {children} <LuArrowRight aria-hidden />
    </button>
  );
}

function IconBtn({ label, children, ...rest }) {
  return (
    <button aria-label={label} style={{ width: 36, height: 36, display: 'grid', placeItems: 'center', borderRadius: 10, border: `1px solid ${C.border}`, backgroundColor: C.surface, color: C.text, fontSize: 16 }} {...rest}>
      {children}
    </button>
  );
}

const ellipsis = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const rowStyle = { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: `1px solid ${C.separator}` };
const thStyle = { padding: '10px 20px', textAlign: 'left', color: C.textMuted, fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' };
const tdStyle = { padding: '12px 20px', color: C.text, verticalAlign: 'top' };
